/**
 * admin 第 25 弾 — CSV parser + validator pure helpers (A-S26 / GAP-A1 parse 基盤)。
 *
 * 旧 admin (`old/apps/admin/src/lib/`) には専用 CSV parser がなかったため、本 sprint で
 * minimum scope の RFC 4180 準拠 parser を新規実装。papaparse 等の dep は禁止 (本 sprint scope)。
 *
 * scope:
 *   - 引用符 (`"..."`) 内の改行 / カンマ / `""` エスケープ対応
 *   - 1 行目 header、以降を row として `Record<string, string>` 化
 *   - 列数不一致は ImportRowError として警告 (parse 自体は継続)
 *   - 空行は skip
 *   - UTF-8 想定 (Shift_JIS は別 sprint、papaparse + jschardet で対応予定)
 *
 * scope 外 (別 sprint):
 *   - 実 submit / Firestore write (callable F24-F29)
 *   - Google Sheets / Drive photos import (GAP-A2 / A3)
 *   - 巨大 CSV の streaming parse (現状は in-memory)
 *   - column mapping UI (header → field 自動推定)
 */

/** import 対象 mode (district 設定の post-* は district.mode に応じて UI 側で絞り込み)。 */
export type ImportMode = 'post-bousai' | 'post-walker' | 'post-ecw' | 'participant' | 'session';

/** 行 / 列単位の警告・エラー。 */
export interface ImportRowError {
  /** 1-indexed CSV 行番号 (header を含む)。 */
  lineNumber: number;
  /** 該当列名 (列単位エラーのとき)。 */
  column?: string;
  message: string;
}

/** ImportPage が描画する preview payload。 */
export interface ImportPreview {
  mode: ImportMode;
  headers: string[];
  rows: Record<string, string>[];
  totalRows: number;
  /** validateImportRow が ok=true を返した行数。 */
  validRows: number;
  errors: ImportRowError[];
}

/** parseCsv の返り値。 */
export interface ParseCsvResult {
  headers: string[];
  rows: Record<string, string>[];
  rowCount: number;
  errors: ImportRowError[];
}

/**
 * RFC 4180 minimum 準拠の CSV parser。
 *
 * 仕様:
 *   - field separator: `,`
 *   - record separator: `\r\n` / `\n` / `\r`
 *   - field を `"..."` で囲った場合、内部の `,` / 改行をそのまま保持
 *   - quoted field 内の `""` は単一の `"` にエスケープ
 *   - 空行 (改行のみ) は skip
 *   - 1 行目を header、以降の行を `Record<header, value>` に map
 *   - row の列数が header と不一致なら ImportRowError 追加 (parse 自体は継続、不足列は ""、超過は捨てる)
 */
export function parseCsv(text: string): ParseCsvResult {
  const errors: ImportRowError[] = [];
  // BOM 除去 (UTF-8 BOM 付き CSV は Excel default)
  let input = text;
  if (input.charCodeAt(0) === 0xfeff) {
    input = input.slice(1);
  }

  // tokenize into records (array of field arrays), tracking source lineNumber for each record
  const records: { fields: string[]; lineNumber: number }[] = [];
  let curField = '';
  let curRecord: string[] = [];
  let recordStartLine = 1;
  let line = 1;
  let inQuote = false;
  let i = 0;
  const len = input.length;

  function commitField() {
    curRecord.push(curField);
    curField = '';
  }

  function commitRecord() {
    // skip 完全に空の行 (1 field かつ "")
    const isEmpty = curRecord.length === 1 && curRecord[0] === '';
    if (!isEmpty) {
      records.push({ fields: curRecord, lineNumber: recordStartLine });
    }
    curRecord = [];
    recordStartLine = line;
  }

  while (i < len) {
    const ch = input[i];
    if (inQuote) {
      if (ch === '"') {
        if (i + 1 < len && input[i + 1] === '"') {
          // escaped quote
          curField += '"';
          i += 2;
          continue;
        }
        inQuote = false;
        i++;
        continue;
      }
      if (ch === '\n') {
        line++;
      } else if (ch === '\r') {
        // count \r\n as one
        line++;
        if (i + 1 < len && input[i + 1] === '\n') {
          curField += '\r\n';
          i += 2;
          continue;
        }
      }
      curField += ch;
      i++;
      continue;
    }
    // not in quote
    if (ch === '"') {
      // field を `"` で開始した場合のみ quote mode に入る (Excel 互換)。
      // 厳密 RFC 4180 では field 先頭以外の `"` は文字扱いだが、minimum scope では先頭判定で十分。
      if (curField === '') {
        inQuote = true;
        i++;
        continue;
      }
      // 中途の `"` は literal として扱う
      curField += ch;
      i++;
      continue;
    }
    if (ch === ',') {
      commitField();
      i++;
      continue;
    }
    if (ch === '\r' || ch === '\n') {
      commitField();
      commitRecord();
      // \r\n は 1 record separator として扱う
      if (ch === '\r' && i + 1 < len && input[i + 1] === '\n') {
        i += 2;
      } else {
        i++;
      }
      line++;
      recordStartLine = line;
      continue;
    }
    curField += ch;
    i++;
  }
  // 末尾 field / record の commit
  if (curField !== '' || curRecord.length > 0) {
    commitField();
    commitRecord();
  }

  if (inQuote) {
    errors.push({
      lineNumber: recordStartLine,
      message: '引用符 (") が閉じていない CSV です。最後のレコードを安全に解釈できません。',
    });
  }

  if (records.length === 0) {
    return { headers: [], rows: [], rowCount: 0, errors };
  }

  const headerRecord = records[0]!;
  const headers = headerRecord.fields.map((h) => h.trim());
  const rows: Record<string, string>[] = [];

  for (let r = 1; r < records.length; r++) {
    const rec = records[r]!;
    if (rec.fields.length !== headers.length) {
      errors.push({
        lineNumber: rec.lineNumber,
        message: `列数が header と不一致です (header=${headers.length}, row=${rec.fields.length})`,
      });
    }
    const row: Record<string, string> = {};
    for (let c = 0; c < headers.length; c++) {
      const key = headers[c]!;
      row[key] = rec.fields[c] ?? '';
    }
    rows.push(row);
  }

  return { headers, rows, rowCount: rows.length, errors };
}

/* ===== mode 別 required column / validation 仕様 ===== */

/** 各 mode の required column (どちらか / 全部 等の条件は validator 内で表現)。 */
const MODE_REQUIRED: Record<ImportMode, { all?: string[]; anyOf?: string[][] }> = {
  'post-bousai': {
    all: ['lat', 'lng', 'createdAt'],
    anyOf: [['title', 'comment']],
  },
  'post-walker': {
    all: ['lat', 'lng', 'createdAt'],
    anyOf: [['title', 'comment']],
  },
  'post-ecw': {
    all: ['lat', 'lng', 'createdAt'],
    anyOf: [['title', 'comment']],
  },
  participant: {
    all: ['nickname'],
  },
  session: {
    all: ['name', 'code'],
  },
};

/** 1 行の validation。 */
export function validateImportRow(
  row: Record<string, string>,
  mode: ImportMode,
): { ok: boolean; errors: string[] } {
  const errors: string[] = [];
  const spec = MODE_REQUIRED[mode];

  for (const col of spec.all ?? []) {
    const v = row[col];
    if (v === undefined || v.trim() === '') {
      errors.push(`必須列 "${col}" が空です`);
    }
  }
  for (const group of spec.anyOf ?? []) {
    const hasAny = group.some((col) => {
      const v = row[col];
      return v !== undefined && v.trim() !== '';
    });
    if (!hasAny) {
      errors.push(`必須列のいずれか (${group.join(' / ')}) が空です`);
    }
  }

  // post-* は lat / lng の number + range check
  if (mode === 'post-bousai' || mode === 'post-walker' || mode === 'post-ecw') {
    const latRaw = row['lat'];
    const lngRaw = row['lng'];
    if (latRaw !== undefined && latRaw.trim() !== '') {
      const lat = Number(latRaw);
      if (!Number.isFinite(lat)) {
        errors.push(`lat が number として parse できません ("${latRaw}")`);
      } else if (lat < -90 || lat > 90) {
        errors.push(`lat が範囲外です (${lat}, 期待: -90..90)`);
      }
    }
    if (lngRaw !== undefined && lngRaw.trim() !== '') {
      const lng = Number(lngRaw);
      if (!Number.isFinite(lng)) {
        errors.push(`lng が number として parse できません ("${lngRaw}")`);
      } else if (lng < -180 || lng > 180) {
        errors.push(`lng が範囲外です (${lng}, 期待: -180..180)`);
      }
    }
  }

  return { ok: errors.length === 0, errors };
}

/**
 * parseCsv + validateImportRow を組み合わせて ImportPreview を構築 (UI から呼び出し)。
 *
 * row エラーは ImportRowError として errors に集約。validRows / totalRows は ImportPage の
 * 上部サマリに利用される。
 */
export function buildImportPreview(text: string, mode: ImportMode): ImportPreview {
  const parsed = parseCsv(text);
  const errors: ImportRowError[] = [...parsed.errors];
  let validRows = 0;

  parsed.rows.forEach((row, idx) => {
    // header が 1 行目なので、row 0 は CSV 2 行目 (lineNumber = idx + 2)
    const lineNumber = idx + 2;
    const v = validateImportRow(row, mode);
    if (v.ok) {
      validRows++;
    } else {
      for (const msg of v.errors) {
        errors.push({ lineNumber, message: msg });
      }
    }
  });

  return {
    mode,
    headers: parsed.headers,
    rows: parsed.rows,
    totalRows: parsed.rowCount,
    validRows,
    errors,
  };
}
